import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DOMAIN_RULE_CATEGORIES,
  DOMAIN_RULE_CATEGORY_LABELS,
  DOMAIN_RULE_FREQUENCIES,
  DOMAIN_RULE_FREQUENCY_LABELS,
  DOMAIN_RULE_SEVERITIES,
  DOMAIN_RULE_SEVERITY_LABELS,
  effectiveRuleThreshold,
  formatRelative,
  formatRuleThreshold,
  formatRuleThresholdValue,
  parseTeamsMessage,
  readRuleThresholdInputs,
  rulePermission,
  ruleThresholds,
  type DomainRuleCategory,
  type DomainRuleFrequency,
  type DomainRuleSeverity,
  type RuleThresholdProblem,
  type RuleThresholdUnit,
} from '@codepass/shared';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Modal,
  Select,
  Skeleton,
  Textarea,
} from '@/components/ui';
import { EmptyState, MetricCard, PageHeader } from '@/components/domain';
import { TeamsMessageEditor } from '@/components/TeamsMessageEditor';
import type { DomainRuleItem } from '@/types';

/** Alvorsgrad styrer farven. Samme skala som alarmerne, så en kritisk regel ser kritisk ud. */
const SEVERITY_TONE: Record<DomainRuleSeverity, 'info' | 'warning' | 'danger'> = {
  info: 'info',
  advarsel: 'warning',
  kritisk: 'danger',
};

export function AdminRulesPage() {
  const queryClient = useQueryClient();
  /*
   * 'Se regler' uden 'Ændr regler': kortene uden Redigér og Slå til/fra. Auditoprydning is
   * 'Ændr opbevaring af audit-log' instead - see `rulePermission`.
   */
  const { can } = useAuth();
  const canEditRule = (rule: DomainRuleItem): boolean => can(rulePermission(rule.name));
  const canEdit = can('rules.manage') || can('audit.retention');
  const [message, setMessage] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<'alle' | DomainRuleCategory>('alle');
  /** Reglen der redigeres. `undefined` = formularen er lukket. */
  const [editing, setEditing] = useState<DomainRuleItem | undefined>(undefined);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'domain-rules'],
    queryFn: () => api.get<{ data: DomainRuleItem[] }>('/domain-rules'),
  });

  const invalidate = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ['admin', 'domain-rules'] });

  const toggle = useMutation({
    mutationFn: (rule: DomainRuleItem) =>
      api.put(`/domain-rules/${rule.id}`, { is_active: !rule.is_active }),
    onSuccess: async (_result, rule) => {
      setMessage(`"${rule.title}" er ${rule.is_active ? 'slået fra' : 'slået til'}.`);
      await invalidate();
    },
    onError: (cause) =>
      setMessage(cause instanceof ApiError ? cause.message : 'Kunne ikke opdatere reglen.'),
  });

  const rules = data?.data ?? [];
  const term = search.trim().toLowerCase();
  const matches = rules.filter(
    (rule) =>
      (category === 'alle' || rule.category === category) &&
      (term.length === 0 ||
        rule.name.toLowerCase().includes(term) ||
        rule.title.toLowerCase().includes(term)),
  );

  /** Kategorierne i katalogets rækkefølge, så listen ikke hopper rundt mellem hentninger. */
  const grouped = DOMAIN_RULE_CATEGORIES.map((key) => ({
    category: key,
    rules: matches.filter((rule) => rule.category === key),
  })).filter((group) => group.rules.length > 0);

  const active = rules.filter((rule) => rule.is_active).length;
  const critical = rules.filter((rule) => rule.severity === 'kritisk').length;

  return (
    <>
      {/*
        Ingen knapper til at oprette, og ingen til at slette. Regler kan konfigureres og slås
        til og fra herfra, men selve kataloget er ikke noget portalen laver: en regel uden en
        evaluering bag sit navn er et kort der ligner et værn uden at være det. Nye regler
        kommer med en implementering i src/rules/ og en række i codepass_domain_rules — ikke
        fra en formular. Og ud kommer de ikke: 'Slå fra' er vejen, se RuleCard.
      */}
      <PageHeader
        title="Regler"
        description={
          canEdit
            ? 'Detekteringsregler fra codepass_domain_rules.'
            : 'Detekteringsregler fra codepass_domain_rules. Din rolle kan se reglerne, men ikke ændre dem.'
        }
      />

      {message ? (
        <Alert tone="info" className="mb-4">
          {message}
        </Alert>
      ) : null}

      {error ? (
        <Alert tone="error" className="mb-4">
          Kunne ikke hente regler.
        </Alert>
      ) : null}

      {rules.length > 0 ? (
        <div className="mb-5 grid gap-4 sm:grid-cols-3">
          <MetricCard label="Regler i alt" value={rules.length} />
          <MetricCard
            label="Aktive"
            value={active}
            hint={active === rules.length ? 'Alle slået til' : `${rules.length - active} slået fra`}
            tone={active === rules.length ? 'success' : 'warning'}
          />
          <MetricCard
            label="Kritiske"
            value={critical}
            tone={critical > 0 ? 'danger' : 'neutral'}
          />
        </div>
      ) : null}

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : rules.length === 0 ? (
        <EmptyState
          title="Ingen regler endnu"
          description="Kataloget i codepass_domain_rules er tomt. En regel kan konfigureres her, når den findes, men håndhæves først, når der ligger en evaluering bag dens navn."
        />
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Input
              className="max-w-sm"
              placeholder="Søg på navn eller titel"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <Select
              className="w-48"
              value={category}
              onChange={(event) => setCategory(event.target.value as 'alle' | DomainRuleCategory)}
            >
              <option value="alle">Alle kategorier</option>
              {DOMAIN_RULE_CATEGORIES.map((key) => (
                <option key={key} value={key}>
                  {DOMAIN_RULE_CATEGORY_LABELS[key]}
                </option>
              ))}
            </Select>
            <span className="text-xs text-ink-muted">
              {matches.length} af {rules.length}
            </span>
          </div>

          {grouped.length === 0 ? (
            <EmptyState title="Ingen regler matcher" />
          ) : (
            <div className="space-y-6">
              {grouped.map((group) => (
                <section key={group.category}>
                  <h2 className="mb-2 text-sm font-semibold">
                    {DOMAIN_RULE_CATEGORY_LABELS[group.category]}
                    <span className="ml-2 font-normal text-ink-muted">({group.rules.length})</span>
                  </h2>
                  <div className="grid gap-3 lg:grid-cols-2">
                    {group.rules.map((rule) => (
                      <RuleCard
                        key={rule.id}
                        rule={rule}
                        busy={toggle.isPending && toggle.variables?.id === rule.id}
                        onEdit={canEditRule(rule) ? () => setEditing(rule) : null}
                        onToggle={canEditRule(rule) ? () => toggle.mutate(rule) : null}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </>
      )}

      {/*
        Formularen monteres foerst naar den aabnes, og forsvinder helt naar den lukkes. Det er
        det der nulstiller felterne: kladden ligger i useState, og en initialisering koerer kun
        ved mount. Stod komponenten altid i traeet, ville den vise den forrige regels vaerdier -
        ogsaa efter en redigering man afbrød.
      */}
      {editing !== undefined ? (
        <RuleFormModal
          rule={editing}
          onClose={() => setEditing(undefined)}
          onSaved={(title) => setMessage(`"${title}" er opdateret.`)}
        />
      ) : null}
    </>
  );
}

function RuleCard({
  rule,
  busy,
  onEdit,
  onToggle,
}: {
  rule: DomainRuleItem;
  busy: boolean;
  /** Null uden 'Ændr regler' - saa er kortet til at laese. */
  onEdit: (() => void) | null;
  onToggle: (() => void) | null;
}) {
  return (
    <Card className={rule.is_active ? undefined : 'opacity-70'}>
      <CardHeader className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <CardTitle>{rule.title}</CardTitle>
          <p className="mt-0.5 font-mono text-xs text-ink-muted">{rule.name}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone={SEVERITY_TONE[rule.severity]}>
            {DOMAIN_RULE_SEVERITY_LABELS[rule.severity]}
          </Badge>
          <Badge tone={rule.is_active ? 'success' : 'neutral'}>
            {rule.is_active ? 'Aktiv' : 'Slået fra'}
          </Badge>
        </div>
      </CardHeader>

      <CardBody className="space-y-3">
        <p className="text-xs text-ink-muted">{rule.description}</p>

        {/*
          Beskeden vises som det, den bliver til — ikke som råtekst med @{} og [[ ]] i. Kortet
          skal kunne læses som "det her sker, når reglen udløses", og markeringerne er
          syntaks, ikke indhold. Redigér åbner den rå tekst.
        */}
        <TeamsMessageSummary message={rule.teams_message} />

        <ThresholdSummary rule={rule} />

        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="neutral">{DOMAIN_RULE_FREQUENCY_LABELS[rule.frequency]}</Badge>

          {onEdit ? (
            <Button size="sm" variant="secondary" onClick={onEdit}>
              Redigér
            </Button>
          ) : null}

          {/*
            Slå fra er vejen ud, og der er ingen anden. En regel kan ikke slettes — hverken
            herfra eller gennem API'et, som ikke har en DELETE-rute: rækken ligger i det skema
            Codepass deler med de andre OneCode-apps, og en regel der er slået fra kan ses og
            slås til igen, hvor en slettet regel ikke efterlader noget spor af, at værnet
            manglede. Begrundelsen står i routes/domain-rules.ts.
          */}
          {onToggle ? (
            <Button size="sm" variant="ghost" loading={busy} onClick={onToggle}>
              {rule.is_active ? 'Slå fra' : 'Slå til'}
            </Button>
          ) : null}

          <span className="ml-auto text-xs text-ink-muted">
            {rule.last_run ? `Kørt ${formatRelative(rule.last_run)}` : 'Aldrig kørt'}
          </span>
        </div>
      </CardBody>
    </Card>
  );
}

/**
 * Teams-beskeden på regelkortet, i én linje.
 *
 * Uden brugerlisten kan omtalerne ikke slås op til navne, og de vises derfor som deres
 * adresse. Kortet skal ikke hente 23 brugere pr. regel for at kunne skrive et fornavn —
 * formularen gør det, når nogen redigerer.
 */
function TeamsMessageSummary({ message }: { message: string | null }) {
  const parsed = parseTeamsMessage(message);

  if (parsed.lines.length === 0) {
    return <p className="text-xs text-ink-muted">Ingen Teams-besked.</p>;
  }

  return (
    <div className="rounded border border-line bg-canvas px-2 py-1.5">
      <p className="line-clamp-2 text-xs">
        {parsed.lines.join(' ').replaceAll(/<at>([^<]*)<\/at>/g, '@$1')}
      </p>
      {parsed.unknownMentions.length > 0 ? (
        <p className="mt-1 text-xs text-warning">
          {parsed.unknownMentions.length} ukendt{' '}
          {parsed.unknownMentions.length === 1 ? 'adresse' : 'adresser'}
        </p>
      ) : null}
    </div>
  );
}

/** Enheden bag et tærskelfelt, som den står efter tallet. */
const THRESHOLD_UNIT_SUFFIX: Record<RuleThresholdUnit, string> = {
  pct: '%',
  timer: 'timer',
  dage: 'dage',
  maaneder: 'måneder',
};

/**
 * Reglens tærskler på kortet: 'Advarsel ved 90 % · Kritisk ved 100 %'.
 *
 * Viser den **gældende** værdi og ikke kun den gemte: en tom nøgle betyder, at reglen regner
 * med sin egen standard, og kortet skal sige hvad reglen gør — ikke hvad der tilfældigvis
 * står i kolonnen. Se `effectiveRuleThreshold`.
 *
 * Ukendte nøgler nævnes ikke. De bevares ved gemning, men de siger ingenting om, hvad reglen
 * gør, og kortet er stedet hvor man læser netop det.
 */
function ThresholdSummary({ rule }: { rule: DomainRuleItem }) {
  const defs = ruleThresholds(rule.name);
  if (defs.length === 0) return null;

  return (
    <p className="text-xs text-ink-muted">
      {defs
        .map((def) => {
          const value = effectiveRuleThreshold(rule.name, def.key, rule.thresholds);
          return value === null ? null : formatRuleThreshold(def, value);
        })
        .filter(Boolean)
        .join(' · ')}
    </p>
  );
}

/**
 * Tærskelfelterne i formularen.
 *
 * Felterne kommer af `RULE_THRESHOLDS` i @codepass/shared og ikke af rækken: kolonnen er
 * `jsonb` og siger ikke selv, hvilke nøgler reglen læser. Et felt pr. nøgle frem for en
 * JSON-editor, fordi en tastefejl i en nøgle ikke fejler — reglen falder tilbage på sin
 * standard, og grænsen man lige satte, gælder ingenting.
 *
 * **Nøgler kataloget ikke kender, vises alligevel.** Tabellen deles med de andre
 * OneCode-apps, og en ukendt nøgle kan være en anden apps. Den skal kunne rettes, og den må
 * ikke forsvinde, fordi den ikke stod i et felt: gemning sender hele objektet.
 */
function ThresholdFields({
  rule,
  values,
  problems,
  onChange,
}: {
  rule: string;
  values: Record<string, string>;
  problems: RuleThresholdProblem[];
  onChange: (values: Record<string, string>) => void;
}) {
  const defs = ruleThresholds(rule);
  const unknown = Object.keys(values)
    .filter((key) => !defs.some((def) => def.key === key))
    .sort();

  if (defs.length === 0 && unknown.length === 0) {
    return (
      <p className="text-xs text-ink-muted">
        Reglen har ingen tærskler. Den udløses af en overskridelse eller en manglende kobling, ikke
        af en grænse man kan sætte.
      </p>
    );
  }

  const problemFor = (key: string): string | undefined =>
    problems.find((problem) => problem.key === key)?.message;

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {defs.map((def) => (
        <div key={def.key} className="space-y-1">
          <Label htmlFor={`rule-threshold-${def.key}`}>{def.label}</Label>
          <div className="flex items-center gap-2">
            <Input
              id={`rule-threshold-${def.key}`}
              type="number"
              inputMode={def.integer ? 'numeric' : 'decimal'}
              step={def.integer ? 1 : 'any'}
              min={def.min}
              max={def.max}
              placeholder={def.fallback === null ? '' : String(def.fallback)}
              value={values[def.key] ?? ''}
              onChange={(event) => onChange({ ...values, [def.key]: event.target.value })}
            />
            <span className="shrink-0 text-xs text-ink-muted">
              {THRESHOLD_UNIT_SUFFIX[def.unit]}
            </span>
          </div>
          {def.hint ? <p className="text-xs text-ink-muted">{def.hint}</p> : null}
          {/*
            Hvad et tomt felt betyder, står kun når feltet *er* tomt. Standarden er reglens
            egen konstant — se `numberThreshold` i API'et — og et tomt felt er derfor en
            beslutning om at bruge den, ikke en manglende værdi.
          */}
          {/*
            En taerskel uden standard (`fallback: null`) er "ingen graense" - fx en entitet,
            auditoprydningen aldrig sletter. Den siger det med sin egen tekst.
          */}
          {(values[def.key] ?? '').trim().length === 0 ? (
            <p className="text-xs text-ink-muted">
              {def.fallback === null
                ? (def.emptyHint ?? 'Tomt felt = ingen grænse.')
                : `Tomt felt = reglens standard, ${formatRuleThresholdValue(def.unit, def.fallback)}.`}
            </p>
          ) : null}
          {problemFor(def.key) ? (
            <p className="text-xs text-danger">{problemFor(def.key)}</p>
          ) : null}
        </div>
      ))}

      {unknown.map((key) => (
        <div key={key} className="space-y-1">
          <Label htmlFor={`rule-threshold-${key}`} className="font-mono text-xs">
            {key}
          </Label>
          <Input
            id={`rule-threshold-${key}`}
            type="number"
            inputMode="decimal"
            step="any"
            value={values[key] ?? ''}
            onChange={(event) => onChange({ ...values, [key]: event.target.value })}
          />
          <p className="text-xs text-warning">
            Ingen af Codepass&apos; regler læser denne tærskel. Den bevares, som den står.
          </p>
          {problemFor(key) ? <p className="text-xs text-danger">{problemFor(key)}</p> : null}
        </div>
      ))}
    </div>
  );
}

/**
 * Redigér en regel.
 *
 * Kun redigering. En regel kommer af en implementering i src/rules/ og en række i
 * codepass_domain_rules, ikke af en formular, så der er intet oprettelsestilfælde at dele
 * felterne med. `name` er reglens nøgle og kan ikke ændres, når rækken først findes.
 */
function RuleFormModal({
  rule,
  onClose,
  onSaved,
}: {
  rule: DomainRuleItem;
  onClose: () => void;
  onSaved: (title: string) => void;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  /**
   * Kladden sættes én gang, ved mount. Komponenten monteres først når formularen åbnes, så
   * det er også der felterne nulstilles - se kaldstedet.
   */
  const [draft, setDraft] = useState({
    title: rule.title,
    description: rule.description,
    category: rule.category,
    severity: rule.severity,
    frequency: rule.frequency,
    // Feltet er nullable i databasen, men et textarea vil have en streng.
    teams_message: rule.teams_message ?? '',
  });

  /**
   * Tærsklerne som tekst og ikke som tal.
   *
   * Et tomt felt er en gyldig tilstand — nøglen udgår, og reglen bruger sin standard — og det
   * kan et `number` ikke rumme. Teksterne oversættes ved gemning af
   * `readRuleThresholdInputs`, som også er den der afgør, hvad der er i vejen.
   *
   * Nøglerne kommer fra rækken, så en nøgle kataloget ikke kender, stadig kan rettes og ikke
   * forsvinder ved gemning: kroppen sender hele objektet.
   */
  const [thresholds, setThresholds] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      Object.entries(rule.thresholds ?? {}).map(([key, value]) => [key, String(value)]),
    ),
  );

  const parsedThresholds = readRuleThresholdInputs(rule.name, thresholds);

  const save = useMutation({
    // `name` sendes ikke: serveren tager ikke imod det, og feltet er låst i formularen.
    mutationFn: () =>
      api.put<DomainRuleItem>(`/domain-rules/${rule.id}`, {
        ...draft,
        /*
         * Tomt objekt gemmes som NULL. 'Ingen tærskler' skal være én værdi og ikke to — det
         * er den samme afgørelse serveren træffer, se thresholdsSchema i API'et.
         */
        thresholds:
          Object.keys(parsedThresholds.values).length > 0 ? parsedThresholds.values : null,
      }),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'domain-rules'] });
      onSaved(saved.title);
      onClose();
    },
    onError: (cause) =>
      setError(cause instanceof ApiError ? cause.message : 'Kunne ikke gemme reglen.'),
  });

  /*
   * Gem er slået fra, så længe en tærskel er i vejen. Serveren afviser den samme værdi med
   * den samme besked — kontrollen her sparer bare turen, og den er ikke det eneste værn.
   */
  const complete =
    draft.title.trim().length > 0 &&
    draft.description.trim().length > 0 &&
    parsedThresholds.problems.length === 0;

  // `open` er altid sandt: komponenten findes kun, naar formularen er aabnet - se kaldstedet.
  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title="Redigér regel"
      description="Navnet er reglens tekniske nøgle og kan ikke ændres."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annullér
          </Button>
          <Button disabled={!complete} loading={save.isPending} onClick={() => save.mutate()}>
            Gem ændringer
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="rule-name">Navn</Label>
          {/*
            Vises, men kan ikke rettes. Navnet er den nøgle reglens fil i src/rules/ slås op
            på, så et andet navn ville gøre reglen til en anden regel. Serveren tager ikke
            imod feltet.
          */}
          <Input id="rule-name" className="font-mono text-xs" value={rule.name} disabled readOnly />
        </div>

        <div className="space-y-1">
          <Label htmlFor="rule-title">Titel</Label>
          <Input
            id="rule-title"
            placeholder="Manglende faktura på nået milepæl"
            value={draft.title}
            onChange={(event) => setDraft({ ...draft, title: event.target.value })}
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="rule-description">Beskrivelse</Label>
          <Textarea
            id="rule-description"
            rows={3}
            placeholder="Hvad reglen ser efter, og hvorfor det er værd at reagere på."
            value={draft.description}
            onChange={(event) => setDraft({ ...draft, description: event.target.value })}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1">
            <Label htmlFor="rule-category">Kategori</Label>
            <Select
              id="rule-category"
              value={draft.category}
              onChange={(event) =>
                setDraft({ ...draft, category: event.target.value as DomainRuleCategory })
              }
            >
              {DOMAIN_RULE_CATEGORIES.map((key) => (
                <option key={key} value={key}>
                  {DOMAIN_RULE_CATEGORY_LABELS[key]}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="rule-severity">Alvorsgrad</Label>
            <Select
              id="rule-severity"
              value={draft.severity}
              onChange={(event) =>
                setDraft({ ...draft, severity: event.target.value as DomainRuleSeverity })
              }
            >
              {DOMAIN_RULE_SEVERITIES.map((key) => (
                <option key={key} value={key}>
                  {DOMAIN_RULE_SEVERITY_LABELS[key]}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="rule-frequency">Kører</Label>
            <Select
              id="rule-frequency"
              value={draft.frequency}
              onChange={(event) =>
                setDraft({ ...draft, frequency: event.target.value as DomainRuleFrequency })
              }
            >
              {DOMAIN_RULE_FREQUENCIES.map((key) => (
                <option key={key} value={key}>
                  {DOMAIN_RULE_FREQUENCY_LABELS[key]}
                </option>
              ))}
            </Select>
          </div>
        </div>

        {/*
          Tærsklerne. Hvor stor en andel af et loft eller et estimat der skal til, før nogen
          skal vide det, er en forretningsbeslutning — derfor står den her og ikke i koden.
          Ændringen gælder fra næste kørsel; der skal ikke deployes.
        */}
        <div className="space-y-1 border-t border-line pt-3">
          <Label>Tærskler</Label>
          <ThresholdFields
            rule={rule.name}
            values={thresholds}
            problems={parsedThresholds.problems}
            onChange={setThresholds}
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="rule-teams-message">Besked i Teams-kanalen</Label>
          <TeamsMessageEditor
            id="rule-teams-message"
            value={draft.teams_message}
            onChange={(teams_message) => setDraft({ ...draft, teams_message })}
            /* Kortets overskrift i testen. Falder tilbage, så en ny regel uden titel kan testes. */
            title={draft.title.trim() || 'Testbesked fra Codepass'}
          />
        </div>

        {error ? <p className="text-xs text-danger">{error}</p> : null}
      </div>
    </Modal>
  );
}
